import { useEffect, useRef, useState } from "react";
import { ZERO_DELTA, inverseDelta } from "./shiftGeometry";

const SETTLE_FALLBACK_MS = 1500;

const useFullCourseShift = () => {
  const [phase, setPhase] = useState("idle");
  const [delta, setDelta] = useState(ZERO_DELTA);
  const [lastAppliedDelta, setLastAppliedDelta] = useState(null);
  const activeRef = useRef(false);
  const phaseRef = useRef("idle");
  const pendingAppliedRef = useRef(null);
  const settleTimerRef = useRef(null);

  const clearSettleTimer = () => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  };

  const moveTo = (next) => {
    if (next !== "settling") clearSettleTimer();
    activeRef.current = next !== "idle";
    phaseRef.current = next;
    setPhase(next);
  };

  useEffect(() => clearSettleTimer, []);

  const enter = (initialDelta = ZERO_DELTA) => {
    setDelta(initialDelta);
    moveTo("adjusting");
  };

  const cancel = () => {
    setDelta(ZERO_DELTA);
    moveTo("idle");
  };

  const reset = () => setDelta(ZERO_DELTA);

  const beginProcessing = () => moveTo("processing");

  const failProcessing = () => moveTo("adjusting");

  const completeProcessing = (appliedDelta) => {
    setLastAppliedDelta(appliedDelta);
    setDelta(ZERO_DELTA);
    moveTo("reviewing");
  };

  const completeSettling = () => {
    if (phaseRef.current !== "settling") return;
    clearSettleTimer();
    const applied = pendingAppliedRef.current;
    pendingAppliedRef.current = null;
    completeProcessing(applied);
  };

  const beginSettling = (appliedDelta) => {
    pendingAppliedRef.current = appliedDelta;
    clearSettleTimer();
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      completeSettling();
    }, SETTLE_FALLBACK_MS);
    moveTo("settling");
  };

  const shiftAgain = () => enter(ZERO_DELTA);

  const revertLast = () => {
    if (lastAppliedDelta) enter(inverseDelta(lastAppliedDelta));
  };

  const finish = () => cancel();

  return {
    phase,
    delta,
    lastAppliedDelta,
    activeRef,
    enter,
    cancel,
    reset,
    setDelta,
    beginProcessing,
    failProcessing,
    completeProcessing,
    beginSettling,
    completeSettling,
    shiftAgain,
    revertLast,
    finish,
  };
};

export default useFullCourseShift;
